import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Zap, 
  Users, 
  MapPin, 
  ArrowLeft,
  LogOut,
  TrendingUp,
  Sun,
  Home,
  Battery,
  Calendar,
  UserPlus,
  Activity,
  MessageCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, logout, isAuthenticated } = useAuth();
  const [community, setCommunity] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    fetchCommunityData();
  }, [id, user]);

  const fetchCommunityData = async () => {
    try {
      const [communityRes, membersRes] = await Promise.all([
        axios.get(`${API_URL}/api/communities/${id}`),
        axios.get(`${API_URL}/api/communities/${id}/members`)
      ]);
      
      setCommunity(communityRes.data);
      setMembers(membersRes.data);
      
      // Check if user is member
      if (user) {
        const membership = membersRes.data.find(m => m.user_id === user.id);
        setIsMember(!!membership);
      }
      
      // Generate chart data
      generateChartData();
    } catch (error) {
      console.error('Error fetching community:', error);
      toast.error('Communauté non trouvée');
      navigate('/communities');
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = () => {
    const data = [];
    const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    days.forEach(day => {
      data.push({
        day,
        production: Math.floor(Math.random() * 100) + 50,
        consumption: Math.floor(Math.random() * 80) + 30,
      });
    });
    setChartData(data);
  };

  const handleJoin = async () => {
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour rejoindre une communauté');
      navigate('/login');
      return;
    }
    
    setJoining(true);
    try {
      await axios.post(`${API_URL}/api/communities/${id}/join`);
      toast.success('Vous avez rejoint la communauté !');
      setIsMember(true);
      fetchCommunityData();
    } catch (error) {
      console.error('Error joining community:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'adhésion');
    } finally {
      setJoining(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  if (!community) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link active">Communautés</Link>
              <Link to="/simulator" className="nav-link">Simulateur</Link>
            </div>
            
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  <div className="hidden sm:block text-right">
                    <p className="text-sm font-medium text-foreground">{user?.name}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={handleLogout}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => navigate('/login')}
                  className="btn-primary"
                  data-testid="login-btn"
                >
                  Connexion
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/communities"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux communautés
          </Link>

          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-3xl p-8 mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="community-name">
                  {community.name}
                </h1>
                <div className="flex items-center gap-2 text-muted-foreground mb-4">
                  <MapPin className="w-4 h-4" />
                  <span>{community.location}</span>
                </div>
                <p className="text-muted-foreground max-w-xl">{community.description}</p>
              </div>
              
              {!isMember ? (
                <Button 
                  onClick={handleJoin}
                  disabled={joining || community.member_count >= community.max_members}
                  className="btn-primary whitespace-nowrap"
                  data-testid="join-btn"
                >
                  {joining ? (
                    <div className="loader w-5 h-5 border-2 border-white/30 border-t-white" />
                  ) : community.member_count >= community.max_members ? (
                    'Communauté complète'
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      Rejoindre la communauté
                    </>
                  )}
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => navigate(`/communities/${id}/chat`)}
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary hover:text-white"
                    data-testid="chat-btn"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Chat
                  </Button>
                  <Button
                    onClick={() => navigate(`/communities/${id}/flows`)}
                    className="btn-primary"
                    data-testid="energy-flows-btn"
                  >
                    <Activity className="w-5 h-5 mr-2" />
                    Gérer les flux
                  </Button>
                  <div className="flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full">
                    <Users className="w-5 h-5" />
                    <span className="font-medium">Membre</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="stats-card" data-testid="stat-members">
              <CardContent className="p-6 text-center">
                <Users className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{community.member_count}</p>
                <p className="text-sm text-muted-foreground">Membres</p>
              </CardContent>
            </Card>
            
            <Card className="stats-card" data-testid="stat-price">
              <CardContent className="p-6 text-center">
                <TrendingUp className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{community.price_per_kwh}€</p>
                <p className="text-sm text-muted-foreground">par kWh</p>
              </CardContent>
            </Card>
            
            <Card className="stats-card" data-testid="stat-production">
              <CardContent className="p-6 text-center">
                <Sun className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{community.total_production_kwh?.toFixed(0) || 0}</p>
                <p className="text-sm text-muted-foreground">kWh produits</p>
              </CardContent>
            </Card>
            
            <Card className="stats-card" data-testid="stat-consumption">
              <CardContent className="p-6 text-center">
                <Battery className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{community.total_consumption_kwh?.toFixed(0) || 0}</p>
                <p className="text-sm text-muted-foreground">kWh consommés</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Energy Chart */}
            <Card className="lg:col-span-2 stats-card" data-testid="energy-chart">
              <CardHeader>
                <CardTitle>Activité de la semaine</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="day" 
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        unit=" kWh"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px'
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="production" 
                        name="Production" 
                        fill="#22c55e" 
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar 
                        dataKey="consumption" 
                        name="Consommation" 
                        fill="#3b82f6" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Members List */}
            <Card className="stats-card" data-testid="members-list">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Membres
                  <span className="text-sm font-normal text-muted-foreground">
                    {community.member_count}/{community.max_members}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {members.map((member) => (
                    <div 
                      key={member.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-muted/50"
                      data-testid={`member-${member.id}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        member.user_type === 'producer' ? 'bg-yellow-100' : 'bg-blue-100'
                      }`}>
                        {member.user_type === 'producer' ? (
                          <Sun className="w-5 h-5 text-yellow-600" />
                        ) : (
                          <Home className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {member.user_name}
                          {member.user_id === community.owner_id && (
                            <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              Admin
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {member.user_type === 'producer' ? 'Producteur' : 'Consommateur'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Owner Info */}
          <Card className="stats-card mt-8" data-testid="owner-info">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Créée par</p>
                  <p className="font-medium text-foreground">{community.owner_name}</p>
                </div>
                <div className="ml-auto flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">
                    {new Date(community.created_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default CommunityDetail;
